import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {
  ThumbUp,
  ThumbDown,
  ExpandLess,
  Feedback as FeedbackIcon,
} from '@material-ui/icons';
import { useApi } from '@backstage/core-plugin-api';
import { entityFeedbackApiRef } from '@backstage-community/plugin-entity-feedback';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

interface TechDocsFeedbackAddonProps {
  title?: string;
}

type FeedbackType = 'good' | 'issue' | null;
type FeedbackCategory = 'documentation-quality' | 'bug-report' | 'content-error' | 'improvement-suggestion' | '';

export const TechDocsFeedbackAddon = (props: TechDocsFeedbackAddonProps) => {
  const { title = 'Rate this documentation' } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(null);
  const [feedbackCategory, setFeedbackCategory] = useState<FeedbackCategory>('');
  const [feedbackText, setFeedbackText] = useState('');
  const [consent, setConsent] = useState<boolean | null>(null);
  const [isAnonymous, setIsAnonymous] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const feedbackApi = useApi(entityFeedbackApiRef);
  const techDocsPage = useTechDocsReaderPage();
  const cardRef = useRef<HTMLDivElement>(null);

  // Get entity reference and document path from TechDocs context
  // Force lowercase to match entity-feedback convention
  const entityRef = stringifyEntityRef({
    kind: techDocsPage.entityRef.kind.toLowerCase(),
    namespace: (techDocsPage.entityRef.namespace || 'default').toLowerCase(),
    name: techDocsPage.entityRef.name.toLowerCase(),
  });
  const documentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const documentTitle = typeof document !== 'undefined' ? document.title : '';

  // Auto-expand when user selects "issue" to collect more details
  useEffect(() => {
    if (feedbackType === 'issue') {
      setIsExpanded(true);
    }
  }, [feedbackType]);

  // Auto-collapse after inactivity
  useEffect(() => {
    if (isOpen && !feedbackText.trim() && !feedbackCategory) {
      // Only auto-close if user hasn't started typing or selecting category
      const timer = setTimeout(() => {
        setIsOpen(false);
        setIsExpanded(false);
        setFeedbackType(null);
        setFeedbackCategory('');
        setFeedbackText('');
        setConsent(null);
        setIsAnonymous(null);
      }, 10000); // Auto-close after 10 seconds of inactivity

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, feedbackText, feedbackCategory]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Use composedPath to handle shadow DOM properly
      const path = event.composedPath();

      // Check if any element in the path is our card
      const isInsideCard = path.includes(cardRef.current as EventTarget);

      if (isInsideCard) {
        return; // Click inside, do nothing
      }

      // Click is outside the card, close it
      setIsOpen(false);
      setIsExpanded(false);
      setFeedbackType(null);
      setFeedbackCategory('');
      setFeedbackText('');
      setConsent(null);
      setIsAnonymous(null);
    };

    // Add a delay before attaching listener to avoid catching the opening click or button clicks
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 500);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleFeedbackTypeChange = async (type: FeedbackType) => {
    setFeedbackType(type);
    setError(null);

    // For "good" feedback, we can submit immediately with minimal details
    if (type === 'good') {
      setIsSubmitting(true);
      try {
        // Submit positive feedback using recordRating (no Slack notification, no UI display)
        await feedbackApi.recordRating(entityRef, 'like');

        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setIsOpen(false);
        }, 3000);
      } catch (error) {
        console.error('Error submitting feedback:', error);
        setError('Failed to submit feedback. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSubmitDetailedFeedback = async () => {
    if (!feedbackCategory || !feedbackText.trim() || isAnonymous === null) {
      return;
    }
    if (!isAnonymous && consent === null) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      // Submit detailed feedback with TechDocs context
      await feedbackApi.recordResponse(entityRef, {
        response: feedbackText,
        consent: isAnonymous ? false : (consent ?? undefined),
        comments: JSON.stringify({
          featureType: 'Docs', // Automatically set to "Docs" for TechDocs feedback
          feedbackType: feedbackType,
          category: feedbackCategory,
          documentPath: documentPath,
          documentTitle: documentTitle,
          additionalComments: feedbackText,
          isAnonymous: isAnonymous
        })
      });

      // Reset form
      setFeedbackType(null);
      setFeedbackCategory('');
      setFeedbackText('');
      setConsent(null);
      setIsAnonymous(null);
      setIsExpanded(false);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  // If feedback was submitted, show success message briefly
  if (isSubmitted) {
    return (
      <Box style={{ margin: '20px 0', display: 'inline-block' }}>
        <Tooltip title="Thank you for your feedback!" placement="right">
          <IconButton
            style={{
              color: '#4caf50',
              background: 'rgba(76, 175, 80, 0.1)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(76, 175, 80, 0.2)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // If not open, show just the icon with tooltip
  if (!isOpen) {
    return (
      <Box style={{ margin: '20px 0', display: 'inline-block' }}>
        <Tooltip title={title} placement="right">
          <IconButton
            onClick={() => setIsOpen(true)}
            style={{
              color: '#2196f3',
              background: 'rgba(33, 150, 243, 0.08)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(33, 150, 243, 0.2)',
              boxShadow: '0 2px 4px rgba(33, 150, 243, 0.15)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(33, 150, 243, 0.15)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(33, 150, 243, 0.08)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // If open, show the full feedback card
  return (
    <Card
      ref={cardRef}
      style={{
        margin: '20px 0',
        borderLeft: '4px solid #2196f3',
        background: 'rgba(227, 242, 253, 0.9)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(33, 150, 243, 0.2)',
        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)',
        borderRadius: '4px',
      }}
    >
      <CardContent style={{ padding: '16px' }}>
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box display="flex" alignItems="center" style={{ gap: '20px' }}>
              <FeedbackIcon style={{ color: '#2196f3', fontSize: '20px' }} />
              <Typography variant="subtitle2" style={{ color: '#2196f3', fontWeight: 600, fontSize: '14px', fontFamily: 'Helvetica, Arial, sans-serif' }}>
                {title}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setIsOpen(false);
                setIsExpanded(false);
                setFeedbackType(null);
                setFeedbackCategory('');
                setFeedbackText('');
                setConsent(null);
                setIsAnonymous(null);
              }}
              style={{ padding: 4 }}
            >
              <ExpandLess />
            </IconButton>
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: '12px' }} mb={1.5}>
            <Button
              variant={feedbackType === 'good' ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              startIcon={<ThumbUp style={{ fontSize: '16px' }} />}
              onClick={() => handleFeedbackTypeChange('good')}
              disabled={isSubmitting}
              style={{ minWidth: '130px', fontSize: '12px', padding: '6px 12px', fontFamily: 'Helvetica, Arial, sans-serif' }}
            >
              Good Documentation
            </Button>
            <Button
              variant={feedbackType === 'issue' ? 'contained' : 'outlined'}
              color="secondary"
              size="small"
              startIcon={<ThumbDown style={{ fontSize: '16px' }} />}
              onClick={() => handleFeedbackTypeChange('issue')}
              disabled={isSubmitting}
              style={{ minWidth: '110px', fontSize: '12px', padding: '6px 12px', fontFamily: 'Helvetica, Arial, sans-serif' }}
            >
              Found Issues
            </Button>
          </Box>

          <Collapse in={isExpanded || feedbackType === 'issue'}>
            <Box mt={2}>
              {feedbackType === 'issue' && (
                <>
                  <FormControl component="fieldset" fullWidth margin="normal" style={{ marginTop: '12px' }}>
                    <FormLabel component="legend">
                      <Typography variant="subtitle2" style={{ color: '#2196f3', fontWeight: 600, fontSize: '13px', fontFamily: 'Helvetica, Arial, sans-serif' }}>
                        What type of issue did you find?
                      </Typography>
                    </FormLabel>
                    <RadioGroup
                      value={feedbackCategory}
                      onChange={(e) => setFeedbackCategory(e.target.value as FeedbackCategory)}
                      style={{ marginTop: 8 }}
                    >
                      <FormControlLabel
                        value="content-error"
                        control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                        label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>Content Error (incorrect information, typos)</Typography>}
                      />
                      <FormControlLabel
                        value="bug-report"
                        control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                        label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>Bug Report (broken links, missing images)</Typography>}
                      />
                      <FormControlLabel
                        value="documentation-quality"
                        control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                        label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>Documentation Quality (unclear, incomplete)</Typography>}
                      />
                      <FormControlLabel
                        value="improvement-suggestion"
                        control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                        label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>Improvement Suggestion</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>

                  <Box mt={2}>
                    <FormLabel component="legend" required={false}>
                      <Typography variant="subtitle2" style={{ color: '#2196f3', fontWeight: 600, fontSize: '13px', fontFamily: 'Helvetica, Arial, sans-serif', marginBottom: '8px' }}>
                        Please describe the issue in detail <span style={{ color: '#f44336' }}>*</span>
                      </Typography>
                    </FormLabel>
                    <TextField
                      multiline
                      rows={3}
                      variant="outlined"
                      fullWidth
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder="For example: Line 15 has incorrect API endpoint, or the code example is missing import statements..."
                      InputProps={{
                        style: {
                          backgroundColor: '#ffffff',
                          fontSize: '12px',
                          fontFamily: 'Helvetica, Arial, sans-serif'
                        }
                      }}
                      inputProps={{
                        style: { color: '#333', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }
                      }}
                      FormHelperTextProps={{
                        style: { color: '#999', fontSize: '11px' }
                      }}
                    />
                  </Box>

                  <FormControl component="fieldset" fullWidth margin="normal" style={{ marginTop: '12px' }}>
                    <FormLabel component="legend" required={false}>
                      <Typography variant="subtitle2" style={{ color: '#2196f3', fontWeight: 600, fontSize: '13px', fontFamily: 'Helvetica, Arial, sans-serif' }}>
                        Do you want to submit this feedback anonymously? <span style={{ color: '#f44336' }}>*</span>
                      </Typography>
                    </FormLabel>
                    <RadioGroup
                      value={isAnonymous === null ? '' : isAnonymous.toString()}
                      onChange={(e) => {
                        setIsAnonymous(e.target.value === 'true');
                        // Reset consent when changing anonymous option
                        if (e.target.value === 'true') {
                          setConsent(null);
                        }
                      }}
                      style={{ marginTop: 8 }}
                    >
                      <FormControlLabel
                        value="true"
                        control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                        label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>Yes, submit anonymously</Typography>}
                      />
                      <FormControlLabel
                        value="false"
                        control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                        label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>No, show my name</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>

                  {isAnonymous === false && (
                    <FormControl component="fieldset" fullWidth margin="normal" style={{ marginTop: '12px' }}>
                      <FormLabel component="legend" required={false}>
                        <Typography variant="subtitle2" style={{ color: '#2196f3', fontWeight: 600, fontSize: '13px', fontFamily: 'Helvetica, Arial, sans-serif' }}>
                          Is it okay to contact you regarding this feedback? <span style={{ color: '#f44336' }}>*</span>
                        </Typography>
                      </FormLabel>
                      <RadioGroup
                        value={consent === null ? '' : consent.toString()}
                        onChange={(e) => setConsent(e.target.value === 'true')}
                        style={{ marginTop: 8 }}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                          label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>Yes, you may contact me</Typography>}
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio size="small" style={{ color: '#2196f3', padding: '6px' }} />}
                          label={<Typography style={{ color: '#666', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>No, please do not contact me</Typography>}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}

                  {error && (
                    <Box mt={2}>
                      <Typography style={{ color: '#f44336', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>
                        {error}
                      </Typography>
                    </Box>
                  )}

                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                      onClick={handleSubmitDetailedFeedback}
                      color="primary"
                      variant="contained"
                      disabled={isSubmitting || !feedbackCategory || !feedbackText.trim() || isAnonymous === null || (isAnonymous === false && consent === null)}
                      size="small"
                      style={{ fontSize: '12px', padding: '6px 16px', fontFamily: 'Helvetica, Arial, sans-serif' }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  );
};